import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './CookieConsent.css';

const COOKIE_CONSENT_KEY = 'cookie_consent';
const CONSENT_VERSION = '1'; // Increment when consent requirements change

/**
 * Cookie Consent Banner
 *
 * Displays a banner asking users to accept cookies on first visit.
 * Stores consent in localStorage.
 */
const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user has already given consent
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);

    if (!consent) {
      // No consent stored, show banner
      setShowBanner(true);
    } else {
      try {
        const parsed = JSON.parse(consent);
        // Check if consent version matches current version
        if (parsed.version !== CONSENT_VERSION) {
          // Consent version outdated, show banner again
          setShowBanner(true);
        }
      } catch {
        // Invalid consent data, show banner
        setShowBanner(true);
      }
    }
  }, []);

  const handleAcceptAll = () => {
    const consent = {
      version: CONSENT_VERSION,
      essential: true,
      analytics: true,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));
    setShowBanner(false);
  };

  const handleEssentialOnly = () => {
    const consent = {
      version: CONSENT_VERSION,
      essential: true,
      analytics: false,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));
    setShowBanner(false);
  };

  const handleLearnMore = (e) => {
    e.preventDefault();
    navigate('/legal/cookies');
  };

  if (!showBanner) {
    return null;
  }

  return (
    <div className="cookie-consent">
      <div className="cookie-consent__content">
        <div className="cookie-consent__text">
          <p className="cookie-consent__message">
            We use cookies and similar technologies to provide the Service, understand how you use it,
            and improve your experience.
          </p>
          <a href="/legal/cookies" onClick={handleLearnMore} className="cookie-consent__link">
            Learn more
          </a>
        </div>
        <div className="cookie-consent__actions">
          <button
            className="cookie-consent__btn cookie-consent__btn--secondary"
            onClick={handleEssentialOnly}
          >
            Essential Only
          </button>
          <button
            className="cookie-consent__btn cookie-consent__btn--primary"
            onClick={handleAcceptAll}
          >
            Accept All
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Helper function to check if user has given analytics consent
 */
export const hasAnalyticsConsent = () => {
  try {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) return false;
    const parsed = JSON.parse(consent);
    return parsed.analytics === true;
  } catch {
    return false;
  }
};

/**
 * Helper function to check if consent has been given (any type)
 */
export const hasConsentBeenGiven = () => {
  return localStorage.getItem(COOKIE_CONSENT_KEY) !== null;
};

export default CookieConsent;
